/** The notification choices on My settings - ADR-0092 (in-app only) and ADR-0212.
 *
 * **The Email column is the person's own saved choice** (G-P8-08), not the state of the channel. It
 * said Off for every kind whatever had been saved, which read as the product ignoring a choice the
 * person had just made. In-app notifications are always on; email notifications are not being sent
 * yet (no sender is set up for this workspace), so what the two switches record is what the person
 * wants for when they are, and the info control says so. The server stores one email choice and one
 * summary choice per person per workspace, not one per kind, so the same cell repeats down the
 * column and the header says it is all kinds (G-Wcontrib-21's other half): a per-kind switch would
 * be a control with nothing behind it. The column follows the **saved** choice, not a ticked box. Everything that explains that - what a summary is, that the choice is yours
 * in this workspace only, that password-reset and invitation emails are unchanged - is behind the
 * info control (`infoText.ts`, `account.notifications`).
 *
 * The inbox itself, the work queue and the review queue are Action items now (the bell opens it), so
 * the links and counts that used to sit under this form are gone from it, not moved: a second list of
 * what is waiting beside the first is a number that can disagree with it.
 */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";

import { useMe } from "../api/auth";
import { useNotificationPreferences, useSaveNotificationPreferences } from "../api/notificationPreferences";
import type { NotificationPreferenceChange, NotificationPreferences } from "../api/notificationPreferences";
import { Loaded, WriteFailed } from "../components/Loaded";
import { GridTable, StatusChip } from "../components/ui";
import type { GridColumn } from "../components/ui";
import own from "./Profile.module.css";
import styles from "./Screen.module.css";

/** The kinds of notification, by the family of the kind the server serves (`NotificationKind`'s part
 * before the dot). In-app is always on and email is off; neither is a stored per-kind choice. */
const KINDS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "approval", label: "Approvals" },
  { id: "task", label: "Tasks" },
  { id: "period", label: "Reporting periods" },
  { id: "filing", label: "Filing deadlines" },
  { id: "source", label: "Data gaps" },
];

/** The Email cell: the saved choice, in a word. `null` while the choice has not been read (or could
 * not be): a dash, never an invented Off - the read failure has its own rendering below. */
function emailChoice(saved: NotificationPreferences | undefined): ReactNode {
  if (!saved) return "-";
  if (!saved.email_notifications) {
    return (
      <StatusChip tone="neutral" size="sm">
        Off
      </StatusChip>
    );
  }
  return (
    <StatusChip tone="info" size="sm">
      {saved.email_digest ? "Summary" : "On"}
    </StatusChip>
  );
}

function columns(saved: NotificationPreferences | undefined): GridColumn<(typeof KINDS)[number]>[] {
  return [
    { id: "kind", header: "Notification", cell: (row) => row.label },
    {
      id: "app",
      header: "In app",
      cell: () => (
        <StatusChip tone="ok" size="sm">
          On
        </StatusChip>
      ),
    },
    { id: "email", header: "Email, all kinds", cell: () => emailChoice(saved) },
  ];
}

function PreferenceForm({ preference }: { preference: NotificationPreferences }) {
  const save = useSaveNotificationPreferences();
  const [checked, setChecked] = useState(preference.email_notifications);
  const [digest, setDigest] = useState(preference.email_digest);
  const [saved, setSaved] = useState(false);
  const unchanged = checked === preference.email_notifications && digest === preference.email_digest;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    // Only what differs from what is saved: a write that names a choice the person did not touch
    // would restate it, and the server treats a repeat as no change anyway.
    const change: NotificationPreferenceChange = {};
    if (checked !== preference.email_notifications) change.email_notifications = checked;
    if (digest !== preference.email_digest) change.email_digest = digest;
    save.mutate(change, {
      onSuccess: (next) => {
        setChecked(next.email_notifications);
        setDigest(next.email_digest);
        setSaved(true);
      },
    });
  }

  return (
    <form className={own.form} onSubmit={submit}>
      <label>
        <input
          type="checkbox"
          checked={checked}
          disabled={save.isPending}
          onChange={(event) => {
            setChecked(event.target.checked);
            setSaved(false);
            save.reset();
          }}
        />{" "}
        Receive notification emails for this workspace
      </label>
      <label>
        <input
          type="checkbox"
          checked={digest}
          disabled={save.isPending}
          onChange={(event) => {
            setDigest(event.target.checked);
            setSaved(false);
            save.reset();
          }}
        />{" "}
        Send one summary instead of an email for each item
      </label>
      {preference.email_suppressed_reason ? (
        <p role="alert" data-testid="email-suppressed">
          Notification emails to your address have been stopped because the mail provider reported
          {preference.email_suppressed_reason === "complaint"
            ? " a complaint about a message sent to it."
            : " that a message to it could not be delivered."}{" "}
          Your in-app notifications are not affected. There is not yet a way to clear this yourself;
          tell EmitIQ support if the address is correct.
        </p>
      ) : null}
      <WriteFailed error={save.error} of="your notification preference" />
      {saved && !save.isPending ? (
        <p role="status" className={own.saved}>
          Notification preference saved. No notification email was sent.
        </p>
      ) : null}
      <span>
        <button type="submit" className={styles.primary} disabled={save.isPending || unchanged}>
          {save.isPending ? "Saving…" : "Save notification preference"}
        </button>
      </span>
    </form>
  );
}

/** The table of kinds and the two switches. The section heading and its info control are the page's. */
export function NotificationSettings() {
  const identity = useMe();
  const preference = useNotificationPreferences();
  return (
    <>
      <GridTable
        caption="What you are notified of, and how"
        columns={columns(preference.data)}
        rows={KINDS}
        rowKey={(row) => row.id}
        rowNumbers={false}
        testId="notification-kinds"
      />
      <Loaded query={preference} of="Your notification preference">
        {(saved) => (
          <PreferenceForm key={`${identity.data?.user_id}:${identity.data?.org_id}`} preference={saved} />
        )}
      </Loaded>
    </>
  );
}
